import React from "react";
import {
  Anchor,
  Clock3,
  ExternalLink,
  LogOut,
  QrCode,
  Umbrella,
  X,
} from "lucide-react";
import {
  IonContent,
  IonMenu,
  IonMenuToggle,
} from "@ionic/react";
import { useLocation } from "react-router-dom";
import { cn } from "@/shared/utils/cn";
import { usePatrolConfig } from "@/features/patrol/hooks";
import { useReportPermissions } from "@/features/reports/hooks";
import { useAuth } from "@/features/auth/hooks";
import { config } from "@/lib/config";
import { useIonRouter } from "@/app/IonicContextProvider";

type NavigationItem = {
  name: string;
  icon: typeof Clock3;
  href?: string;
  onClick?: () => void;
  section?: "primary" | "secondary";
};

const baseNavigation: NavigationItem[] = [
  { name: "Shifts", href: "/shifts", icon: Clock3, section: "primary" },
  { name: "Leave", href: "/leave", icon: Umbrella, section: "primary" },
];

function Menu() {
  const router = useIonRouter();
  const { logout } = useAuth();
  const location = useLocation();
  const { data: patrolConfig } = usePatrolConfig();
  const { data: reportPermissions } = useReportPermissions();
  const path = location?.pathname ?? "/";

  const managerAppUrl = config.app.managerAppUrl;
  const canViewReports =
    !!reportPermissions &&
    (reportPermissions.view_shift_reports || reportPermissions.view_patrol_reports) &&
    !!managerAppUrl;

  const navigation: NavigationItem[] = [
    ...baseNavigation,
    ...(patrolConfig !== null && patrolConfig !== undefined
      ? [{ name: "Patrol", href: "/patrol/scan", icon: QrCode } as NavigationItem]
      : []),
    ...(canViewReports
      ? [
          {
            name: "View reports",
            icon: ExternalLink,
            onClick: () => {
              if (managerAppUrl) {
                window.open(managerAppUrl, "_blank", "noopener,noreferrer");
              }
            },
            section: "secondary",
          } as NavigationItem,
        ]
      : []),
    {
      name: "Logout",
      icon: LogOut,
      onClick: () => {
        logout();
        router.push("/login", "root", "replace");
      },
      section: "secondary",
    } as NavigationItem,
  ].map((item) => ({
    section: (item.section ?? "secondary") as "primary" | "secondary",
    ...item,
  }));

  const primaryItems = navigation.filter((item) => item.section === "primary");
  const secondaryItems = navigation.filter((item) => item.section === "secondary");

  const handleNavigation = (item: NavigationItem) => {
    if (item.onClick) {
      item.onClick();
      return;
    }
    const href = item.href;
    if (!href) return;
    router.push(href, "root", "replace");
  };

  return (
    <IonMenu contentId="main-content" side="start" swipeGesture={true}>
      <IonContent className="bg-slate-100">
        <div
          className="min-h-full bg-white px-4 pb-4 pt-[max(1rem,var(--ion-safe-area-top))]"
          data-testid="menu-shell"
        >
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500 shadow-sm">
                <Anchor className="h-5 w-5 text-white" />
              </div>
              <span className="text-2xl font-bold tracking-tight text-slate-900">
                TightShip
              </span>
            </div>
            <IonMenuToggle autoHide={true}>
              <button
                type="button"
                className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition-colors hover:bg-slate-200"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </IonMenuToggle>
          </div>

          <nav className="space-y-1.5">
            {[...primaryItems, ...secondaryItems].map((item, index) => {
              const isActive =
                !!item.href &&
                (path === item.href ||
                  (item.href === "/tasks" && path.startsWith("/tasks")) ||
                  (item.href === "/shifts" && path.startsWith("/shifts")) ||
                  (item.href === "/leave" && path.startsWith("/leave")) ||
                  (item.href === "/patrol/scan" && path.startsWith("/patrol")));

              const shouldShowDivider =
                index === primaryItems.length && secondaryItems.length > 0;

              return (
                <React.Fragment key={item.name}>
                  {shouldShowDivider ? (
                    <div className="my-3 h-px w-full bg-slate-200" />
                  ) : null}
                  <IonMenuToggle autoHide={true}>
                    <button
                      onClick={() => handleNavigation(item)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors",
                        isActive
                          ? "bg-slate-100 text-slate-900"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-800"
                      )}
                      data-testid={`menu-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="text-xl leading-tight font-medium">
                        {item.name}
                      </span>
                    </button>
                  </IonMenuToggle>
                </React.Fragment>
              );
            })}
          </nav>
        </div>
      </IonContent>
    </IonMenu>
  );
}

export default Menu;